import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import './eLogbook.css'
import collegeConfig from '../../config/collegeConfig';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import {
  FaPlus,
  FaEye,
  FaBookMedical,
  FaClipboardList,
  FaSearch,
  FaSave,
  FaTrash,
  FaCheck,
  FaClock,
  FaExclamationTriangle,
  FaArrowLeft,
  FaEdit,
  FaFilter,
  FaTimes,
  FaSortAmountDown,
  FaSortAmountUp,
  FaDownload,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarAlt,
  FaPaperclip,
  FaFilePdf,
  FaImage,
  FaSpinner
} from 'react-icons/fa';
import { generateLogbookPDF } from './pdfGenerator';

const ITEMS_PER_PAGE = 15;

const AttachmentItem = ({ attachment, token, baseURL }) => {
  const [loading, setLoading] = React.useState(false);
  const [viewerOpen, setViewerOpen] = React.useState(false);
  const [viewerUrl, setViewerUrl] = React.useState('');

  const handleOpen = async () => {
    setLoading(true);
    try {
      const t = token || localStorage.getItem('token');
      const url = baseURL || collegeConfig.api.baseURL;
      const res = await fetch(`${url}/upload/signed-url?key=${encodeURIComponent(attachment.key)}`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      if (!res.ok) { setLoading(false); return; }
      const data = await res.json();

      if (Capacitor.isNativePlatform() && attachment.type !== 'image') {
        // PDF on native: download to cache and open with FileOpener
        const fileRes = await fetch(data.url);
        const blob = await fileRes.blob();
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = reader.result.split(',')[1];
          const fileName = attachment.name || 'attachment.pdf';
          const result = await Filesystem.writeFile({
            path: fileName,
            data: base64,
            directory: Directory.Cache,
            recursive: true
          });
          try {
            await FileOpener.open({ filePath: result.uri, contentType: 'application/pdf' });
          } catch {
            alert(`File saved. Open from Files app: ${fileName}`);
          }
          setLoading(false);
        };
        reader.readAsDataURL(blob);
        return;
      }

      setViewerUrl(data.url);
      setViewerOpen(true);
    } catch { /* ignore */ }
    setLoading(false);
  };

  return (
    <>
      <div className="attachment-item">
        <div className="attachment-icon">
          {attachment.type === 'image' ? <FaImage /> : <FaFilePdf />}
        </div>
        <span className="attachment-name">{attachment.name}</span>
        <button className="btn-view-attachment" onClick={handleOpen} disabled={loading}>
          {loading ? <FaSpinner className="spin" /> : 'View'}
        </button>
      </div>
      {viewerOpen && (
        <div className="attachment-viewer-overlay" onClick={() => setViewerOpen(false)}>
          <div className="attachment-viewer-content" onClick={e => e.stopPropagation()}>
            <div className="attachment-viewer-header">
              <span className="attachment-viewer-name">{attachment.name}</span>
              <button className="attachment-viewer-close" onClick={() => setViewerOpen(false)}>
                <FaTimes />
              </button>
            </div>
            {attachment.type === 'image'
              ? <img src={viewerUrl} alt={attachment.name} className="attachment-viewer-img" />
              : <iframe src={viewerUrl} title={attachment.name} className="attachment-viewer-iframe" />
            }
          </div>
        </div>
      )}
    </>
  );
};

const ELogbook = ({ onNavigate, parentUser, initialFilter }) => {
  const subscriptionExpired =
    parentUser?.subscription?.status !== 'not_required' &&
    (parentUser?.subscription?.status === 'expired' ||
      (parentUser?.subscription?.expiryDate && new Date(parentUser.subscription.expiryDate) < new Date()));
  const [view, setView] = useState('main');
  const [user, setUser] = useState(null);
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [moduleSearchTerm, setModuleSearchTerm] = useState('');
  const [formData, setFormData] = useState({});
  const [counters, setCounters] = useState({});
  const [submittedEntries, setSubmittedEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [viewingEntry, setViewingEntry] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // { file, preview, name, type, size }
  const [isUploading, setIsUploading] = useState(false);
  
  const [filters, setFilters] = useState({
    moduleId: '',
    sortOrder: 'desc',
    status: '',
    month: new Date().toISOString().slice(0, 7) // Current month in YYYY-MM format
  });
  const [showFilters, setShowFilters] = useState(true); // Changed to true - visible by default
  const [showAllTime, setShowAllTime] = useState(false); // New state for showing all entries
  const [toast, setToast] = useState({ show: false, message: '' });
  const monthInputRef = useRef(null); // Ref for month input
  const listContentRef = useRef(null); // Ref for scroll-to-top on page change
  const initialFilterAppliedRef = useRef(false); // Guards the flagged-entries deep-link mount effect

  // Pagination state
  const [listPage, setListPage] = useState(1);
  const [monthListPages, setMonthListPages] = useState({});

  // Handler for PDF download
  const handleDownloadPDF = async (includeSubmissionDate) => {
    try {
      setShowDownloadOptions(false);
      setIsGeneratingPDF(true);
      setSuccess('Generating PDF...');

      const studentName = `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || 'Student';
      const courseName = course?.name || 'Course';

      // Filter only submitted/approved entries for PDF
      const pdfEntries = submittedEntries.filter(entry =>
        entry.status !== 'draft'
      );

      if (pdfEntries.length === 0) {
        setError('No submitted entries to generate PDF');
        setIsGeneratingPDF(false);
        return;
      }

      // Fetch achievements and dissertation in parallel
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [achievementsRes, dissertationRes] = await Promise.allSettled([
        fetch(`${collegeConfig.api.baseURL}/achievements`, { headers }).then(r => r.json()),
        fetch(`${collegeConfig.api.baseURL}/dissertation/my`, { headers }).then(r => r.json()),
      ]);

      const achievements = achievementsRes.status === 'fulfilled' && achievementsRes.value.success
        ? achievementsRes.value.achievements || []
        : [];
      const dissertation = dissertationRes.status === 'fulfilled' && dissertationRes.value.success
        ? dissertationRes.value.dissertation
        : null;

      await generateLogbookPDF(studentName, courseName, modules, pdfEntries, {
        collegeName: collegeConfig.collegeName,
        shortName: collegeConfig.shortName,
        logo: collegeConfig.logo,
        theme: collegeConfig.theme,
        copyrightYear: collegeConfig.copyrightYear,
        appName: collegeConfig.appName
      }, user, { achievements, dissertation, includeSubmissionDate });

      setSuccess('PDF downloaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // ... rest of the component code remains exactly the same until the list view
  
  // Enhanced navigation function that updates browser history
  const navigateToView = (newView, state = {}) => {
    setView(newView);
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.querySelector('.elogbook-container')?.scrollTo({ top: 0, behavior: 'instant' });

    // Push state to browser history
    const historyState = {
      page: 'elogbook',
      view: newView,
      ...state
    };

    window.history.pushState(historyState, '', `#elogbook-${newView}`);
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state) {
        // If state has elogbook page, handle internal navigation
        if (event.state.page === 'elogbook') {
          setView(event.state.view);
          
          // Restore state based on view
          if (event.state.selectedModule) {
            const module = modules.find(m => m.moduleId === event.state.selectedModule);
            setSelectedModule(module);
          } else {
            setSelectedModule(null);
          }
          
          if (event.state.viewingEntry) {
            const entry = submittedEntries.find(e => e._id === event.state.viewingEntry);
            setViewingEntry(entry);
          } else {
            setViewingEntry(null);
          }
          
          if (event.state.editingEntry) {
            const entry = submittedEntries.find(e => e._id === event.state.editingEntry);
            setEditingEntry(entry);
          } else {
            setEditingEntry(null);
          }
        } else {
          // If not elogbook state and we're on main view, go back to dashboard
          if (view === 'main') {
            if (onNavigate) {
              onNavigate('dashboard');
            }
          }
        }
      } else {
        // No state means we're going back to dashboard level
        if (view === 'main') {
          if (onNavigate) {
            onNavigate('dashboard');
          }
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    // Set initial state when component mounts
    if (!window.history.state || window.history.state.page !== 'elogbook') {
      window.history.pushState({ page: 'elogbook', view: 'main' }, '', '#elogbook-main');
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [view, modules, submittedEntries, onNavigate]);

  const groupFieldsByName = (fields) => {
    const grouped = {};
    const ungrouped = [];
    
    fields.forEach(field => {
      if (field.groupName) {
        if (!grouped[field.groupName]) {
          grouped[field.groupName] = [];
        }
        grouped[field.groupName].push(field);
      } else {
        ungrouped.push(field);
      }
    });
    
    return { grouped, ungrouped };
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!token || !userData) {
      if (onNavigate) {
        onNavigate('login');
      }
      return;
    }
    
    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.role !== 'student') {
        onNavigate('login');
        return;
      }
      setUser(parsedUser);
      fetchStudentCourse(parsedUser);
    } catch (error) {
      console.error('Error parsing user data:', error);
      if (onNavigate) {
        onNavigate('login');
      }
    }
  }, [onNavigate]);

  const fetchStudentCourse = async (userData) => {
    try {
      const token = localStorage.getItem('token');
      const userId = userData.id || userData._id;
      
      const response = await fetch(`${collegeConfig.api.baseURL}/students/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.student?.course) {
          setCourse(data.student.course);
          
          const courseResponse = await fetch(`${collegeConfig.api.baseURL}/courses/${data.student.course._id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (courseResponse.ok) {
            const courseData = await courseResponse.json();
            setModules(courseData.course.modules || []);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      setError('Failed to load course information');
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const userId = user?.id || user?._id;
      
      if (!userId) return;
      
      const response = await fetch(`${collegeConfig.api.baseURL}/logbook/entries?studentId=${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubmittedEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  }, [user]);

  useEffect(() => {
    if (view === 'list' && user) {
      fetchEntries();
    }
  }, [view, user]);

  // Deep-link from the student dashboard's Repeat/Remedial alert banner
  useEffect(() => {
    if (initialFilter === 'flagged' && !initialFilterAppliedRef.current && user) {
      initialFilterAppliedRef.current = true;
      setFilters(prev => ({ ...prev, status: 'flagged' }));
      setShowAllTime(true);
      navigateToView('list', { fromFlaggedBanner: true });
    }
  }, [initialFilter, user]);


  const getFilteredAndSortedEntries = () => {
    let filtered = [...submittedEntries];
    
    if (filters.moduleId) {
      filtered = filtered.filter(entry => entry.moduleId === filters.moduleId);
    }
    
    if (filters.status === 'flagged') {
      filtered = filtered.filter(entry =>
        entry.status === 'needs_revision' &&
        (['R', 'Re'].includes(entry.review?.facultyDecision) || !!entry.reattemptRequest)
      );
    } else if (filters.status) {
      filtered = filtered.filter(entry => entry.status === filters.status);
    }
    
    // Month filter logic - only apply if not showing all time
    if (filters.month && !showAllTime) {
      const currentMonth = new Date().toISOString().slice(0, 7);
      const selectedMonth = filters.month;
      
      filtered = filtered.filter(entry => {
        const entryMonth = new Date(entry.createdAt).toISOString().slice(0, 7);
        
        if (selectedMonth === currentMonth) {
          // Current month: show current month entries + previous months unverified entries
          if (entryMonth === currentMonth) {
            return true;
          } else if (entryMonth < currentMonth) {
            // Previous month entries - only show if not verified (approved)
            return entry.status !== 'approved';
          }
          return false;
        } else {
          // Previous month selected: show only that month's entries (all statuses)
          return entryMonth === selectedMonth;
        }
      });
    }
    
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return filters.sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  };

  // Reset pagination when filters or mode change
  useEffect(() => {
    setListPage(1);
    setMonthListPages({});
  }, [filters.moduleId, filters.status, filters.month, filters.sortOrder, showAllTime]);

  // Group entries by month for "show all time" view
  const groupEntriesByMonth = (entries) => {
    const grouped = {};
    entries.forEach(entry => {
      const d = new Date(entry.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const name = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!grouped[key]) grouped[key] = { name, entries: [] };
      grouped[key].entries.push(entry);
    });
    return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
  };

  // Helper function to format month
  const formatMonthName = (monthString) => {
    if (!monthString) return '';
    const date = new Date(monthString + '-01');
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const filteredModules = modules.filter(module =>
    module.isActive !== false &&
    (module.name.toLowerCase().includes(moduleSearchTerm.toLowerCase()) ||
    (module.description && module.description.toLowerCase().includes(moduleSearchTerm.toLowerCase())))
  );

  const handleModuleCardClick = (module) => {
    setSelectedModule(module);
    setEditingEntry(null);
    navigateToView('fill-form', { selectedModule: module.moduleId });
    
    if (module.type === 'entries') {
      const initialData = {};
      module.fields.forEach(field => {
        initialData[field.key] = field.type === 'number' ? 0 : '';
      });
      setFormData(initialData);
    } else if (module.type === 'counters') {
      const initialCounters = {};
      module.fields.forEach(field => {
        if (field.type === 'counter') {
          initialCounters[field.key] = 0;
        }
      });
      setCounters(initialCounters);
    }
  };

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCounterChange = (key, value) => {
    const numValue = parseInt(value) || 0;
    setCounters(prev => ({
      ...prev,
      [key]: numValue
    }));
  };

  const validateForm = () => {
    if (!selectedModule) {
      setError('Please select a module');
      return false;
    }

    if (selectedModule.type === 'entries') {
      // At least one field must have a value
      const hasAnyValue = selectedModule.fields.some(field => {
        const val = formData[field.key];
        return val !== undefined && val !== null && val !== '' && val !== 0;
      });
      if (!hasAnyValue) {
        setError('Please fill in at least one field before submitting.');
        return false;
      }
      // Required fields
      for (const field of selectedModule.fields) {
        if (field.required && !formData[field.key]) {
          setError(`"${field.label}" is required.`);
          return false;
        }
      }
    } else if (selectedModule.type === 'counters') {
      const hasAnyCounter = selectedModule.fields.some(field => (counters[field.key] || 0) > 0);
      if (!hasAnyCounter) {
        setError('Please enter a value greater than zero for at least one counter before submitting.');
        return false;
      }
    }

    return true;
  };

  // ── Attachment helpers ──────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;

    const MAX_BEFORE_COMPRESS = 8 * 1024 * 1024; // 8 MB
    const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];

    for (const file of files) {
      if (!ALLOWED.includes(file.type)) {
        setError('Only JPG, PNG, GIF, WEBP images and PDF files are allowed.');
        return;
      }
      if (file.size > MAX_BEFORE_COMPRESS) {
        setError(`"${file.name}" is too large (max 8 MB).`);
        return;
      }
    }

    const processed = await Promise.all(files.map(async (file) => {
      const isImage = file.type.startsWith('image/');
      let finalFile = file;

      if (isImage) {
        try {
          const imageCompression = (await import('browser-image-compression')).default;
          finalFile = await imageCompression(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            onProgress: () => {}
          });
        } catch {
          finalFile = file; // fallback to original if compression fails
        }
      }

      return {
        file: finalFile,
        preview: isImage ? URL.createObjectURL(finalFile) : null,
        name: file.name,
        type: isImage ? 'image' : 'pdf',
        size: finalFile.size
      };
    }));

    setPendingFiles(prev => [...prev, ...processed]);
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => {
      const updated = [...prev];
      if (updated[index].preview) URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const uploadPendingFiles = async () => {
    if (!pendingFiles.length) return [];
    const token = localStorage.getItem('token');
    const uploaded = [];
    for (const pending of pendingFiles) {
      const fd = new FormData();
      fd.append('file', pending.file, pending.name);
      const res = await fetch(`${collegeConfig.api.baseURL}/upload/attachment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error(`Failed to upload ${pending.name}`);
      const data = await res.json();
      uploaded.push(data.attachment);
    }
    return uploaded;
  };

  const resetAttachments = () => {
    pendingFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setPendingFiles([]);
  };
  // ────────────────────────────────────────────────────────────────────

  // afterAction: 'list' | 'modules' | 'stay'
  const handleSubmit = async (isDraftSubmit = false, afterAction = 'list') => {
    if (!validateForm() && !isDraftSubmit) return;

    setError('');
    setIsUploading(true);
    try {
      // Upload any pending files first
      const uploadedAttachments = await uploadPendingFiles();

      const token = localStorage.getItem('token');
      const userId = user.id || user._id;

      const entryData = {
        studentId: userId,
        courseId: course._id,
        moduleId: selectedModule.moduleId,
        entryType: selectedModule.type === 'entries' ? 'single' : 'bulk',
        isDraft: isDraftSubmit,
        status: isDraftSubmit ? 'draft' : 'submitted'
      };

      if (selectedModule.type === 'entries') {
        entryData.entries = [{
          data: formData,
          attachments: uploadedAttachments,
          submittedAt: new Date(),
          status: isDraftSubmit ? 'draft' : 'submitted'
        }];
      } else {
        entryData.counters = counters;
        if (uploadedAttachments.length > 0) {
          entryData.attachments = uploadedAttachments;
        }
      }

      const url = editingEntry
        ? `${collegeConfig.api.baseURL}/logbook/entries/${editingEntry._id}`
        : `${collegeConfig.api.baseURL}/logbook/entries`;

      const response = await fetch(url, {
        method: editingEntry ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(entryData)
      });

      if (response.ok) {
        fetchEntries();
        resetAttachments();

        if (editingEntry || afterAction === 'list') {
          const msg = editingEntry ? 'Entry updated successfully!' : 'Entry submitted successfully!';
          setSuccess(msg);
          navigateToView('list');
          resetForm();
        } else if (afterAction === 'modules') {
          showToast('Entry submitted! Choose your next module.');
          resetForm();
          navigateToView('select-module');
        } else if (afterAction === 'stay') {
          showToast('Entry submitted! Form is ready for your next entry.');
          resetFieldsOnly();
        }
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to submit entry');
      }
    } catch (error) {
      console.error('Error submitting entry:', error);
      setError('Error connecting to server');
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = (entry) => {
    const module = modules.find(m => m.moduleId === entry.moduleId);
    if (!module) {
      setError('Module not found');
      return;
    }
    
    setSelectedModule(module);
    setEditingEntry(entry);
    
    if (entry.entryType === 'single' && entry.entries && entry.entries[0]) {
      const entryData = entry.entries[0].data;
      const formDataObj = {};
      if (entryData instanceof Map) {
        entryData.forEach((value, key) => {
          formDataObj[key] = value;
        });
      } else {
        Object.assign(formDataObj, entryData);
      }
      setFormData(formDataObj);
    } else if (entry.entryType === 'bulk' && entry.counters) {
      const countersObj = {};
      if (entry.counters instanceof Map) {
        entry.counters.forEach((value, key) => {
          countersObj[key] = value;
        });
      } else {
        Object.assign(countersObj, entry.counters);
      }
      setCounters(countersObj);
    }
    
    navigateToView('fill-form', { 
      selectedModule: module.moduleId, 
      editingEntry: entry._id 
    });
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${collegeConfig.api.baseURL}/logbook/entries/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setSuccess('Entry deleted successfully');
        fetchEntries();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      setError('Error connecting to server');
    }
  };

  const handleViewDetails = (entry) => {
    setViewingEntry(entry);
    navigateToView('view-details', { viewingEntry: entry._id });
  };

  const resetForm = () => {
    setFormData({});
    setCounters({});
    setSelectedModule(null);
    setModuleSearchTerm('');
    setEditingEntry(null);
  };

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: '' }), 3500);
  };

  // Resets only the field values, keeping the same module open
  const resetFieldsOnly = () => {
    if (selectedModule?.type === 'entries') {
      const initial = {};
      selectedModule.fields.forEach(f => { initial[f.key] = f.type === 'number' ? 0 : ''; });
      setFormData(initial);
    } else if (selectedModule?.type === 'counters') {
      const initial = {};
      selectedModule.fields.forEach(f => { if (f.type === 'counter') initial[f.key] = 0; });
      setCounters(initial);
    }
    setEditingEntry(null);
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={formData[field.key] || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
            required={field.required}
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={formData[field.key] || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
            min={field.validation?.min}
            max={field.validation?.max}
            required={field.required}
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={formData[field.key] || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            required={field.required}
          />
        );
      
      case 'select':
        return (
          <select
            value={formData[field.key] || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'textarea':
        return (
          <textarea
            value={formData[field.key] || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
            rows="4"
            required={field.required}
          />
        );
      
      case 'counter':
        return (
          <div className="counter-field">
            <input
              type="number"
              value={counters[field.key] || 0}
              onChange={(e) => handleCounterChange(field.key, e.target.value)}
              min="0"
            />
            {field.target && (
              <span className="target-info">Target: {field.target}</span>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { icon: <FaClock />, class: 'status-draft', text: 'Draft' },
      submitted: { icon: <FaClock />, class: 'status-submitted', text: 'Submitted' },
      approved: { icon: <FaCheck />, class: 'status-approved', text: 'Approved' },
      rejected: { icon: <FaExclamationTriangle />, class: 'status-rejected', text: 'Rejected' },
      needs_revision: { icon: <FaExclamationTriangle />, class: 'status-rejected', text: 'Needs Revision' },
      under_review: { icon: <FaClock />, class: 'status-pending', text: 'Under Review' }
    };
    
    return badges[status] || badges.submitted;
  };

  const canEdit = (entry) => {
    return entry.status === 'draft' || entry.status === 'needs_revision' || entry.status === 'rejected';
  };

  const canDelete = (entry) => {
    return entry.status === 'draft' || entry.status === 'rejected';
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="elogbook-loading">
        <div className="spinner"></div>
        <p>Loading eLogbook...</p>
      </div>
    );
  }

  if (view === 'view-details' && viewingEntry) {
    const module = modules.find(m => m.moduleId === viewingEntry.moduleId);
    const status = getStatusBadge(viewingEntry.status);
    const { grouped, ungrouped } = module ? groupFieldsByName(module.fields) : { grouped: {}, ungrouped: [] };
    
    return (
      <div className="elogbook-container">
        <div className="elogbook-header">
          <button className="back-button" onClick={() => {
            setViewingEntry(null);
            navigateToView('list');
          }}>
            <FaArrowLeft /> Back to Submissions
          </button>
          <h1>Entry Details</h1>
          <p>{module?.name || 'Unknown Module'}</p>
        </div>

        <div className="entry-details-card">
          <div className="details-header">
            <h2>{module?.name}</h2>
            <div className={`status-badge ${status.class}`}>
              {status.icon} {status.text}
            </div>
          </div>

          <div className="details-meta">
            <div className="meta-item">
              <strong>Submitted:</strong>
              <span>{new Date(viewingEntry.createdAt).toLocaleString()}</span>
            </div>
            {viewingEntry.review && viewingEntry.status === 'approved' && (
              <>
                <div className="meta-item">
                  <strong>Reviewed By:</strong>
                  <span>{viewingEntry.review.reviewerName || 'Unknown'}</span>
                </div>
                <div className="meta-item">
                  <strong>Reviewed On:</strong>
                  <span>{new Date(viewingEntry.review.reviewedAt).toLocaleString()}</span>
                </div>
              </>
            )}
          </div>

          <div className="details-content">
            <h3>Submitted Data</h3>
            {viewingEntry.entryType === 'single' && viewingEntry.entries && viewingEntry.entries[0] && (
              <>
                {ungrouped.length > 0 && (
                  <div className="data-grid">
                    {ungrouped.map(field => {
                      const entryData = viewingEntry.entries[0].data;
                      let value;
                      if (entryData instanceof Map) {
                        value = entryData.get(field.key);
                      } else {
                        value = entryData[field.key];
                      }
                      
                      return (
                        <div key={field.key} className="data-item">
                          <strong>{field.label}:</strong>
                          <span>{value || 'N/A'}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {Object.entries(grouped).map(([groupName, groupFields]) => (
                  <div key={groupName} className="field-group-container" style={{ marginTop: '20px' }}>
                    <div className="field-group-header">
                      <h4>{groupName}</h4>
                    </div>
                    <div className="data-grid" style={{ padding: '15px', background: 'white' }}>
                      {groupFields.map(field => {
                        const entryData = viewingEntry.entries[0].data;
                        let value;
                        if (entryData instanceof Map) {
                          value = entryData.get(field.key);
                        } else {
                          value = entryData[field.key];
                        }
                        
                        return (
                          <div key={field.key} className="data-item">
                            <strong>{field.label}:</strong>
                            <span>{value || 'N/A'}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </>
            )}
            
            {viewingEntry.entryType === 'bulk' && viewingEntry.counters && (
              <div className="data-grid">
                {module?.fields.map(field => {
                  let value;
                  if (viewingEntry.counters instanceof Map) {
                    value = viewingEntry.counters.get(field.key);
                  } else {
                    value = viewingEntry.counters[field.key];
                  }
                  
                  return (
                    <div key={field.key} className="data-item">
                      <strong>{field.label}:</strong>
                      <span>{value || 0}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {(() => {
            const atts = viewingEntry.entryType === 'single'
              ? viewingEntry.entries?.[0]?.attachments
              : viewingEntry.attachments;
            const token = localStorage.getItem('token');
            if (!atts || atts.length === 0) return null;
            return (
              <div className="details-attachments">
                <h3><FaPaperclip /> Attachments</h3>
                <div className="attachments-list">
                  {atts.map((att, i) => (
                    <AttachmentItem
                      key={i}
                      attachment={att}
                      token={token}
                      baseURL={collegeConfig.api.baseURL}
                    />
                  ))}
                </div>
              </div>
            );
          })()}

          {viewingEntry.review && viewingEntry.review.reviewerId && viewingEntry.status === 'approved' && (
            <div className="review-section">
              <h3>Verification Details</h3>
              <div className="review-grid">
                <div className="review-item">
                  <strong>Attempt Activity:</strong>
                  <span>{viewingEntry.review.attemptActivity === 'F' ? 'First/Only' : 
                         viewingEntry.review.attemptActivity === 'R' ? 'Repeat' : 'Remedial'}</span>
                </div>
                <div className="review-item">
                  <strong>Rating:</strong>
                  <span>{viewingEntry.review.rating === 'E' ? 'Exceeds Expectations' : 
                         viewingEntry.review.rating === 'M' ? 'Meets Expectations' : 'Below Expectations'}</span>
                </div>
                <div className="review-item">
                  <strong>Faculty Decision:</strong>
                  <span>{viewingEntry.review.facultyDecision === 'C' ? 'Completed' : 
                         viewingEntry.review.facultyDecision === 'R' ? 'Repeat' : 'Remedial'}</span>
                </div>
                {viewingEntry.review.feedback && (
                  <div className="review-item full-width">
                    <strong>Feedback:</strong>
                    <p>{viewingEntry.review.feedback}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {viewingEntry.reattemptRequest && viewingEntry.status === 'needs_revision' && (
            <div className="review-section">
              <h3>Reattempt Requested</h3>
              <div className="review-grid">
                <div className="review-item">
                  <strong>Requested By:</strong>
                  <span>{viewingEntry.reattemptRequest.requestedByName}</span>
                </div>
                <div className="review-item">
                  <strong>Requested On:</strong>
                  <span>{new Date(viewingEntry.reattemptRequest.requestedAt).toLocaleString()}</span>
                </div>
                {viewingEntry.reattemptRequest.note && (
                  <div className="review-item full-width">
                    <strong>Note:</strong>
                    <p>{viewingEntry.reattemptRequest.note}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {viewingEntry.reviewHistory?.length > 0 && (
            <div className="review-section">
              <h3>Previous Attempt{viewingEntry.reviewHistory.length > 1 ? 's' : ''}</h3>
              {viewingEntry.reviewHistory.map((h, i) => (
                <div key={i} className="review-grid" style={{ marginBottom: 12 }}>
                  <div className="review-item">
                    <strong>Attempt:</strong>
                    <span>
                      #{i + 1}{h.reason === 'reverified' ? ' — Re-verified by different faculty' : h.reason === 'hod_reattempt' ? ' — HOD requested reattempt' : ''}
                    </span>
                  </div>
                  {h.attemptActivity && (
                    <div className="review-item">
                      <strong>Attempt Activity:</strong>
                      <span>{h.attemptActivity === 'F' ? 'First/Only' : h.attemptActivity === 'R' ? 'Repeat' : 'Remedial'}</span>
                    </div>
                  )}
                  {h.rating && (
                    <div className="review-item">
                      <strong>Rating:</strong>
                      <span>{h.rating === 'E' ? 'Exceeds Expectations' : h.rating === 'M' ? 'Meets Expectations' : 'Below Expectations'}</span>
                    </div>
                  )}
                  {h.facultyDecision && (
                    <div className="review-item">
                      <strong>Faculty Decision:</strong>
                      <span>{h.facultyDecision === 'C' ? 'Completed' : h.facultyDecision === 'R' ? 'Repeat' : 'Remedial'}</span>
                    </div>
                  )}
                  {h.feedback && (
                    <div className="review-item full-width">
                      <strong>Feedback:</strong>
                      <p>{h.feedback}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="details-actions">
            {canEdit(viewingEntry) && (
              <button className="btn-edit11" onClick={() => handleEdit(viewingEntry)}>
                <FaEdit /> Edit Entry
              </button>
            )}
            {canDelete(viewingEntry) && (
              <button className="btn-delete11" onClick={() => {
                handleDelete(viewingEntry._id);
                setViewingEntry(null);
                navigateToView('list');
              }}>
                <FaTrash /> Delete Entry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (view === 'main') {
    return (
      <div className="elogbook-container">
        <div className="elogbook-header">
          <button className="back-button" onClick={() => onNavigate && onNavigate('dashboard')}>
            ← Back to Dashboard
          </button>
          <h1>eLogbook</h1>
          <p>Manage your clinical competencies and submissions</p>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="elogbook-options">
          {subscriptionExpired ? (
            <div className="option-card" style={{ opacity: 0.6, cursor: 'not-allowed', background: '#fafafa' }}>
              <div className="option-icon" style={{ background: '#bbb' }}>
                🔒
              </div>
              <h3>Add Competency</h3>
              <p>Renew your subscription to log new entries</p>
            </div>
          ) : (
            <div className="option-card" onClick={() => navigateToView('select-module')}>
              <div className="option-icon" style={{ background: '#48C9B0' }}>
                <FaPlus />
              </div>
              <h3>Add Competency</h3>
              <p>Log new clinical competency entry</p>
            </div>
          )}

          <div className="option-card" onClick={() => navigateToView('list')}>
            <div className="option-icon" style={{ background: '#3498DB' }}>
              <FaClipboardList />
            </div>
            <h3>View Submissions</h3>
            <p>Review your submitted entries</p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'select-module') {
    return (
      <div className="elogbook-container">
        <div className="elogbook-header">
          <button className="back-button" onClick={() => navigateToView('main')}>
            <FaArrowLeft /> Back to Options
          </button>
          <h1>Select Module</h1>
          <p>Choose a module to add your competency entry</p>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="module-selection-container">
          <div className="module-search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search modules by name or description..."
              value={moduleSearchTerm}
              onChange={(e) => setModuleSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="modules-grid">
            {filteredModules.length === 0 ? (
              <div className="no-modules-found">
                <FaBookMedical className="no-modules-icon" />
                <h3>No modules found</h3>
                <p>Try adjusting your search terms</p>
              </div>
            ) : (
              filteredModules.map((module) => (
                <div
                  key={module.moduleId}
                  className="module-card"
                  onClick={() => handleModuleCardClick(module)}
                >
                  <div className="module-card-header">
                    <div className="module-icon">
                      <FaBookMedical />
                    </div>
                  </div>
                  
                  <h3 className="module-card-title">{module.name}</h3>
                  
                  {module.description && (
                    <p className="module-card-description">{module.description}</p>
                  )}
                  
                  {module.requirements && (
                    <div className="module-requirements">
                      <div className="requirement-item">
                        <span className="requirement-label">Min Entries:</span>
                        <span className="requirement-value">{module.requirements.minEntries}</span>
                      </div>
                      <div className="requirement-item">
                        <span className="requirement-label">Total Marks:</span>
                        <span className="requirement-value">{module.requirements.totalMarks}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="module-card-footer">
                    <span className="fields-count">{module.fields.length} fields</span>
                    <FaArrowLeft className="arrow-icon" style={{ transform: 'rotate(180deg)' }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {toast.show && (
          <div className="submission-toast">
            <FaCheck className="toast-icon" />
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  if (view === 'fill-form') {
    const { grouped, ungrouped } = groupFieldsByName(selectedModule.fields);
    
    return (
      <div className="elogbook-container">
        <div className="elogbook-header">
          <button className="back-button" onClick={() => {
            resetForm();
            navigateToView('select-module');
          }}>
            <FaArrowLeft /> Back to Modules
          </button>
          <h1>{editingEntry ? 'Edit Entry' : selectedModule.name}</h1>
          <p>{editingEntry ? 'Update your entry' : 'Fill in the required information'}</p>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="competency-form">
          <div className="form-section">
            <h3>{selectedModule.name}</h3>
            <div className="form-fields">
              {ungrouped.map((field) => (
                <div key={field.key} className="form-group">
                  <label>
                    {field.label}
                    {field.required && <span className="required">*</span>}
                  </label>
                  {renderField(field)}
                </div>
              ))}
              
              {Object.entries(grouped).map(([groupName, groupFields]) => (
                <div key={groupName} className="field-group-container">
                  <div className="field-group-header">
                    <h4>{groupName}</h4>
                  </div>
                  <div className="field-group-content">
                    {groupFields.map((field) => (
                      <div key={field.key} className="form-group grouped-field">
                        <label>
                          {field.label}
                          {field.required && <span className="required">*</span>}
                        </label>
                        {renderField(field)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {selectedModule.allowAttachments && (
              <div className="attachment-upload-section">
                <div className="attachment-upload-header">
                  <FaPaperclip /> Attachments
                  {pendingFiles.length > 0 && (
                    <span className="attachment-count">{pendingFiles.length} file{pendingFiles.length > 1 ? 's' : ''} selected</span>
                  )}
                </div>

                {pendingFiles.length > 0 && (
                  <div className="pending-files-list">
                    {pendingFiles.map((pf, idx) => (
                      <div key={idx} className="pending-file-item">
                        {pf.type === 'image' && pf.preview
                          ? <img src={pf.preview} alt={pf.name} className="pending-file-thumb" />
                          : <div className="pending-file-icon"><FaFilePdf /></div>
                        }
                        <span className="pending-file-name">{pf.name}</span>
                        <span className="pending-file-size">{(pf.size / 1024).toFixed(0)} KB</span>
                        <button className="pending-file-remove" onClick={() => removePendingFile(idx)} title="Remove">
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <label className="attachment-upload-btn">
                  <FaPaperclip /> Add Files
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    disabled={isUploading}
                  />
                </label>
                <small className="attachment-hint">Images are compressed automatically. Max 8 MB per file.</small>
              </div>
            )}

            {editingEntry ? (
              <div className="form-actions">
                <button className="btn-submit" onClick={() => handleSubmit(false, 'list')} disabled={isUploading}>
                  {isUploading ? <><FaSpinner className="spin" /> Uploading...</> : <><FaSave /> Update Entry</>}
                </button>
                <button className="btn-cancel" onClick={() => { resetForm(); navigateToView('list'); }}>
                  Cancel
                </button>
              </div>
            ) : (
              <div className="form-actions-new">
                {isUploading && (
                  <div className="upload-progress-bar">
                    <FaSpinner className="spin" /> Uploading files, please wait…
                  </div>
                )}
                <div className="form-actions-triple">
                  <button className="btn-submit-action btn-action-list" onClick={() => handleSubmit(false, 'list')} disabled={isUploading}>
                    <FaClipboardList className="btn-action-icon" />
                    <div className="btn-action-text">
                      <span>Submit &amp; View Submissions</span>
                      <small>Go to your submissions list</small>
                    </div>
                  </button>
                  <button className="btn-submit-action btn-action-modules" onClick={() => handleSubmit(false, 'modules')} disabled={isUploading}>
                    <FaBookMedical className="btn-action-icon" />
                    <div className="btn-action-text">
                      <span>Submit &amp; Choose Another Module</span>
                      <small>Submit and pick a different module</small>
                    </div>
                  </button>
                  <button className="btn-submit-action btn-action-stay" onClick={() => handleSubmit(false, 'stay')} disabled={isUploading}>
                    <FaPlus className="btn-action-icon" />
                    <div className="btn-action-text">
                      <span>Submit &amp; Add Another Entry</span>
                      <small>Stay here and add one more entry</small>
                    </div>
                  </button>
                </div>
                <button className="btn-cancel" onClick={() => { resetForm(); navigateToView('select-module'); }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {toast.show && (
          <div className="submission-toast">
            <FaCheck className="toast-icon" />
            {toast.message}
          </div>
        )}
      </div>
    );
  }

  if (view === 'list') {
    const filteredEntries = getFilteredAndSortedEntries();

    const renderCard = (entry) => {
      const module = modules.find(m => m.moduleId === entry.moduleId);
      const status = getStatusBadge(entry.status || 'submitted');
      const isFlagged = entry.status === 'needs_revision' &&
        (['R', 'Re'].includes(entry.review?.facultyDecision) || !!entry.reattemptRequest);
      return (
        <div key={entry._id} className={`submission-card ${isFlagged ? 'submission-card-flagged' : ''}`}>
          <div className="submission-header">
            <h4>{module?.name || 'Unknown Module'}</h4>
            <div className={`status-badge ${status.class}`}>
              {status.icon} {status.text}
            </div>
          </div>
          <div className="submission-body">
            <div className="submission-meta">
              <span>Submitted: {new Date(entry.createdAt).toLocaleDateString()}</span>
            </div>
            {(() => {
              const attCount = entry.entryType === 'single'
                ? (entry.entries?.[0]?.attachments?.length || 0)
                : (entry.attachments?.length || 0);
              if (attCount === 0) return null;
              return (
                <div className="attachment-indicator">
                  <FaPaperclip /> {attCount} item{attCount > 1 ? 's' : ''} attached &middot; click <strong>View Details</strong> to see
                </div>
              );
            })()}
            {entry.review && entry.review.reviewerId && entry.status === 'approved' && (
              <div className="review-info">
                <div className="review-details">
                  <p><strong>Reviewed by:</strong> {entry.review.reviewerName}</p>
                  {entry.review.feedback && (
                    <p className="review-feedback"><strong>Feedback:</strong> {entry.review.feedback}</p>
                  )}
                </div>
              </div>
            )}
            <div className="submission-actions">
              <button className="btn-view-details" onClick={() => handleViewDetails(entry)}>
                <FaEye /> View Details
              </button>
              {(entry.status === 'draft' || entry.status === 'needs_revision' || entry.status === 'rejected' || entry.status === 'submitted') && (
                <button className={isFlagged ? 'btn-reattempt' : 'btn-edit-entry'} onClick={() => handleEdit(entry)}>
                  <FaEdit /> {isFlagged ? 'Reattempt' : 'Edit'}
                </button>
              )}
              {(entry.status === 'draft' || entry.status === 'rejected' || entry.status === 'submitted') && (
                <button className="btn-delete-entry" onClick={() => handleDelete(entry._id)}>
                  <FaTrash /> Delete
                </button>
              )}
            </div>
          </div>
        </div>
      );
    };

    const renderPaginationBar = (currentPage, totalPages, onPrev, onNext) => (
      <div className="el-pagination-controls">
        <button className="el-page-btn" disabled={currentPage === 1} onClick={onPrev}>
          <FaChevronLeft /> Prev
        </button>
        <span className="el-page-info">Page {currentPage} of {totalPages}</span>
        <button className="el-page-btn" disabled={currentPage === totalPages} onClick={onNext}>
          Next <FaChevronRight />
        </button>
      </div>
    );

    const scrollToContent = () => {
      setTimeout(() => listContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    };

    const renderFlatPaginated = () => {
      const totalPages = Math.ceil(filteredEntries.length / ITEMS_PER_PAGE);
      const start = (listPage - 1) * ITEMS_PER_PAGE;
      const pageEntries = filteredEntries.slice(start, start + ITEMS_PER_PAGE);
      return (
        <>
          <div className="submissions-grid">{pageEntries.map(renderCard)}</div>
          {totalPages > 1 && renderPaginationBar(
            listPage, totalPages,
            () => { setListPage(p => p - 1); scrollToContent(); },
            () => { setListPage(p => p + 1); scrollToContent(); }
          )}
        </>
      );
    };

    const renderGroupedByMonth = () => {
      const groups = groupEntriesByMonth(filteredEntries);
      return groups.map(([monthKey, monthData]) => {
        const currentPage = monthListPages[monthKey] || 1;
        const totalPages = Math.ceil(monthData.entries.length / ITEMS_PER_PAGE);
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        const pageEntries = monthData.entries.slice(start, start + ITEMS_PER_PAGE);
        return (
          <div key={monthKey} className="el-month-group">
            <div className="el-month-header">
              <FaCalendarAlt />
              <h3>{monthData.name}</h3>
              <span className="el-month-count">{monthData.entries.length} entries</span>
            </div>
            <div className="submissions-grid">{pageEntries.map(renderCard)}</div>
            {totalPages > 1 && renderPaginationBar(
              currentPage, totalPages,
              () => { setMonthListPages(p => ({ ...p, [monthKey]: currentPage - 1 })); scrollToContent(); },
              () => { setMonthListPages(p => ({ ...p, [monthKey]: currentPage + 1 })); scrollToContent(); }
            )}
          </div>
        );
      });
    };

    return (
      <div className="elogbook-container">
        <div className="elogbook-header">
          <button className="back-button" onClick={() => navigateToView('main')}>
            <FaArrowLeft /> Back to Options
          </button>
          <h1>My Submissions</h1>
          <p>View and track your logbook entries</p>
        </div>

        {success && <div className="alert alert-success">{success}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        <div className="filters-container">
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button 
              className="filter-toggle-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter /> Filters {showFilters ? <FaTimes /> : ''}
            </button>
            
            {/* Download PDF Button */}
            <button
              className="btn-download-pdf"
              onClick={() => setShowDownloadOptions(true)}
              disabled={isGeneratingPDF || submittedEntries.filter(e => e.status !== 'draft').length === 0}
            >
              <FaDownload /> {isGeneratingPDF ? 'Generating PDF...' : 'Download Logbook PDF'}
            </button>
          </div>

          {showDownloadOptions && createPortal(
            <div className="download-options-overlay" onClick={() => setShowDownloadOptions(false)}>
              <div className="download-options-modal" onClick={e => e.stopPropagation()}>
                <div className="download-options-header">
                  <h3>Download Logbook PDF</h3>
                  <button className="download-options-close" onClick={() => setShowDownloadOptions(false)}>
                    <FaTimes />
                  </button>
                </div>
                <p className="download-options-subtitle">Choose whether to include the submission date column</p>
                <div className="download-options-actions">
                  <button
                    className="download-options-btn"
                    onClick={() => handleDownloadPDF(true)}
                  >
                    Download with submission date
                  </button>
                  <button
                    className="download-options-btn"
                    onClick={() => handleDownloadPDF(false)}
                  >
                    Download without submission date
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

          {showFilters && (
            <div className="filters-panel">
              <div className="filter-group">
                <label htmlFor="month-filter" style={{ cursor: 'default' }}>Month:</label>
                <div 
                  className="month-input-wrapper"
                  onClick={() => {
                    if (!showAllTime && monthInputRef.current) {
                      monthInputRef.current.focus();
                      monthInputRef.current.showPicker();
                    }
                  }}
                  style={{ cursor: showAllTime ? 'not-allowed' : 'pointer' }}
                >
                  <input
                    ref={monthInputRef}
                    id="month-filter"
                    type="month"
                    value={filters.month}
                    onChange={(e) => setFilters({...filters, month: e.target.value})}
                    max={new Date().toISOString().slice(0, 7)}
                    disabled={showAllTime}
                  />
                </div>
              </div>

              <div className="filter-group">
                <label>Module:</label>
                <select 
                  value={filters.moduleId}
                  onChange={(e) => setFilters({...filters, moduleId: e.target.value})}
                >
                  <option value="">All Modules</option>
                  {modules.map(module => (
                    <option key={module.moduleId} value={module.moduleId}>
                      {module.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Status:</label>
                <select 
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="under_review">Under Review</option>
                  <option value="approved">Approved</option>
                  <option value="needs_revision">Needs Revision</option>
                  <option value="flagged">Repeat/Remedial</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Sort by Date:</label>
                <button 
                  className="sort-btn"
                  onClick={() => setFilters({
                    ...filters, 
                    sortOrder: filters.sortOrder === 'desc' ? 'asc' : 'desc'
                  })}
                >
                  {filters.sortOrder === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />}
                  {filters.sortOrder === 'desc' ? ' Newest First' : ' Oldest First'}
                </button>
              </div>

              <button 
                className="clear-filters-btn"
                onClick={() => setFilters({ 
                  moduleId: '', 
                  sortOrder: 'desc', 
                  status: '',
                  month: new Date().toISOString().slice(0, 7)
                })}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Month Display and Show All Time Option */}
        <div className="entries-info-bar">
          <div className="entries-month-display">
            {showAllTime ? (
              <span className="month-text">Showing all entries (all time)</span>
            ) : (
              <span className="month-text">
                Showing entries for the month: <strong>{formatMonthName(filters.month)}</strong>
              </span>
            )}
          </div>
          <button 
            className={`btn-toggle-all-time ${showAllTime ? 'active' : ''}`}
            onClick={() => setShowAllTime(!showAllTime)}
          >
            {showAllTime ? 'Show Current Month' : 'Show All Time'}
          </button>
        </div>

        <div ref={listContentRef} className="el-list-content">
          {filteredEntries.length === 0 ? (
            <div className="no-submissions">
              <FaBookMedical className="no-submissions-icon" />
              <h3>No submissions found</h3>
              <p>{submittedEntries.length === 0
                ? 'Start by adding your first competency entry'
                : 'Try adjusting your filters'
              }</p>
              {submittedEntries.length === 0 && !subscriptionExpired && (
                <button onClick={() => navigateToView('select-module')} className="btn-add-first">
                  <FaPlus /> Add First Entry
                </button>
              )}
            </div>
          ) : (
            showAllTime ? renderGroupedByMonth() : renderFlatPaginated()
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default ELogbook;